/**
 * GridEventCardSwitch for CalendarGrid
 * Routes CalendarGrid events to appropriate event card variants and tooltips.
 */
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { GridCalendarEvent } from '../types';
import { deriveHourStrings } from '../utils';
import { GridAppointmentCard, GridAppointmentTooltip } from './variants-grid/grid-appointment-card';
import { GridExceptionCard, GridExceptionTooltip } from './variants-grid/grid-exception-card';
import { GridScheduleRequestCard, GridScheduleRequestTooltip } from './variants-grid/grid-schedule-request-card';

dayjs.extend(customParseFormat);
//#region Card

export const renderGridEventCard = (event: GridCalendarEvent): React.ReactNode => {
  const { startHour, endHour } = deriveHourStrings(event);

  // Extract common props
  const commonProps = {
    id: event.id,
    title: event.title ?? '',
    startDate: event.startDate ?? '',
    startHour,
    endHour,
    locationName: event.locationName,
    locationId: event.locationId,
    entityId: event.entityId,
    entityName: event.entityName,
    eventTypeColor: event.eventTypeColor,
    backgroundColor: event.backgroundColor,
    calendarDate: event.calendarDate,
  };

  switch (event.eventType) {
    case 'appointment':
    case 'cancelledAppointment':
      return (
        <GridAppointmentCard
          {...commonProps}
          personStatus={event.personStatus}
          eventStatus={event.eventStatus}
          overlappingIndicatorVariant={event.overlappingIndicatorVariant}
          isYesterdayEvent={event.isYesterdayEvent}
          scheduleRequestEventData={event.scheduleRequestEventData}
        />
      );
    case 'scheduleRequest':
      return (
        <GridScheduleRequestCard
          {...commonProps}
          personStatus={event.personStatus}
          overlappingIndicatorVariant={event.overlappingIndicatorVariant}
          isYesterdayEvent={event.isYesterdayEvent}
          scheduleRequestEventData={event.scheduleRequestEventData}
        />
      );
    case 'exception':
    case 'unavailableBlock':
      return (
        <GridExceptionCard
          {...commonProps}
          title={event.title || 'Out of Office'}
          eventType={event.eventType === 'exception' ? 'exception' : 'break'}
        />
      );
    // Fallback to let the CalendarGrid handle other event types (e.g., workingHoursV3)
    default:
      return null;
  }
};

//#endregion

//#region Tooltip

/** Dispatches to the per-type tooltip component that lives alongside each card
 *  variant, keeping tooltip content co-located with its card design:
 *   - appointment      → GridAppointmentTooltip  (grid-appointment-card.tsx)
 *   - scheduleRequest  → GridScheduleRequestTooltip (grid-schedule-request-card.tsx)
 *   - exception / unavailableBlock → GridExceptionTooltip (grid-exception-card.tsx)
 *   - other types      → Exception tooltip with title fallback
 */
export const renderGridEventTooltip = (event: GridCalendarEvent): React.ReactNode => {
  const { startHour, endHour } = deriveHourStrings(event);

  switch (event.eventType) {
    case 'appointment':
    case 'cancelledAppointment':
      return (
        <GridAppointmentTooltip
          title={event.title ?? ''}
          startHour={startHour}
          endHour={endHour}
          appointmentType={event.appointmentTypeName}
        />
      );
    case 'scheduleRequest':
      return (
        <GridScheduleRequestTooltip
          startHour={startHour}
          endHour={endHour}
          scheduleRequestEventData={event.scheduleRequestEventData}
          appointmentType={event.appointmentTypeName}
        />
      );
    case 'exception':
    case 'unavailableBlock':
      return (
        <GridExceptionTooltip
          title={event.title || (event.eventType === 'exception' ? 'Out of Office' : 'Break')}
          startHour={startHour}
          endHour={endHour}
        />
      );
    default:
      return <GridExceptionTooltip title={event.title ?? ''} startHour={startHour} endHour={endHour} />;
  }
};

//#endregion
